import React, { useContext } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ApiView } from '@e3dc-react/shell/Elements/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChartView, CustomLayout, RechartData } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { Box } from '@mui/material'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { ChartSettings, getChartName, useLegendState } from 'src/Libs/chart-kit'
import SystemChartComparison from 'src/Libs/system-charts/shared/components/SystemChartComparison'
import SystemChart from 'src/Libs/system-charts/shared/components/SystemChart'
import { useUpdateEditedChartSettings } from '../../../chart-kit/editor/hooks'

const useMappedChartStyles = makeStyles()(() => ({
  tablePaper: {
    overflow: 'auto',
    maxHeight: '100%',
    minHeight: '100%',
  },
}))

interface MappedChartProps {
  chartSettingsList: ChartSettings[]
  idx: number
  userId?: number
  showForecast: boolean
  installationId: number
  chartProps: {
    serialno?: string
    rechartData: RechartData
    view: ChartView
    setView: (value: ChartView) => void
    customLayout: CustomLayout
    from: Moment
    setFrom: (value: Moment) => void
    to: Moment
    setTo: (value: Moment) => void
    pieChartTooltipText?: string
  }
  handleSaveChart: (chartSettings: ChartSettings) => void
  dataQa?: string
}

const MappedChart: React.FC<MappedChartProps> = ({
  chartSettingsList,
  idx,
  chartProps,
  userId,
  showForecast,
  installationId,
  handleSaveChart,
  dataQa,
}: MappedChartProps) => {
  const { deleteChart } = useUpdateEditedChartSettings()
  const { classes } = useMappedChartStyles()
  const { t } = useCustomTranslation()
  const { serialno, rechartData, view, customLayout, from, to, pieChartTooltipText } = chartProps
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)

  const customLayoutId = customLayout.id ?? idx
  const { saveLegendState, hideLegend } = useLegendState(`${customLayoutId}`)

  const savedSettings = chartSettingsList.find(item => {
    const chartIsSavedInUserSettings = String(item.id) === String(customLayout.id)
    const chartBelongsToTheSelectedInstallation =
      Number(item.installationId) === Number(installationId) && Number(item.storageSerialNumber) === Number(serialno)

    return chartIsSavedInUserSettings && chartBelongsToTheSelectedInstallation
  })

  const savedLayout = savedSettings
    ? {
        ...customLayout,
        views: savedSettings.views ?? customLayout.views,
        title: savedSettings.name ?? customLayout.title,
        traces: savedSettings.traces ?? customLayout.traces,
      }
    : { ...customLayout }

  if (savedLayout && savedLayout?.views?.[view] === 'comparison') {
    return (
      <Box key={String(customLayout.title) + '-' + (customLayout.id || 'index' + idx)}>
        <SystemChartComparison
          storageId={serialno}
          view={view}
          to={to}
          from={from}
          rechartData={rechartData}
          customLayout={savedLayout}
          handleSaveChart={handleSaveChart}
          handleDeleteChart={id => {
            if (serialno) deleteChart({ chartId: id, chartSettingsList, userId, installationId, storageSerialNumber: serialno })
          }}
          maxHeight={savedLayout.maxHeight}
          pieChartTooltipText={pieChartTooltipText}
          isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
          toggleHideLegend={() => saveLegendState()}
        />
      </Box>
    )
  }

  return (
    <>
      <Box key={String(customLayout.title) + '-' + (customLayout.id || 'index' + idx)}>
        <SystemChart
          view={view}
          to={to}
          from={from}
          showForecast={showForecast}
          rechartData={rechartData}
          loadingState={rechartData.loadingState}
          customLayout={savedLayout}
          handleSaveChart={handleSaveChart}
          handleDeleteChart={id => {
            if (serialno) deleteChart({ chartId: id, chartSettingsList, userId, installationId, storageSerialNumber: serialno })
          }}
          maxHeight={savedLayout.maxHeight}
          pieChartTooltipText={pieChartTooltipText}
          isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
          hideLegend={hideLegend}
          toggleHideLegend={() => saveLegendState()}
          dataQa={dataQa}
        />
      </Box>
      {customLayout.tableProps && (
        <Box key={String(customLayout.title) + '-table-' + (customLayout.id || 'index' + idx)}>
          <Paper title={getChartName(customLayout, t)} className={classes.tablePaper}>
            <ApiView
              fetchProps={{ url: customLayout.tableProps.url, endpoint: customLayout.tableProps.endpoint, limit: 3 }}
              tableColumns={customLayout.tableProps.tableColumns}
              listProps={{}}
              onlyTable
              headerElevation={0}
              footerElevation={0}
              bodyElevation={0}
              onSelect={customLayout.tableProps?.onClick}
            />
          </Paper>
        </Box>
      )}
    </>
  )
}

export default MappedChart
